// ⚠️  NOTE: Real pricing is controlled by the backend API (see backend/configuration/fee-config.js)
// This file's DEFAULT_FEE_CONFIG is only a fallback if the API is unreachable.
// You almost never need to update this unless you want the fallback to match the backend immediately.
// The SINGLE SOURCE OF TRUTH for all payment amounts is backend/configuration/fee-config.js

// If you change pricing, update only backend/configuration/fee-config.js.
// This file is used by: static-site/src/services/config.service.js and com.southvanmed/src/services/config.service.js

// ---

// Fee config service - fetches fee configuration from backend API
import { useState, useEffect } from 'react';
import config from '../config';

const DEFAULT_FEE_CONFIG = {
  SICK_NOTE: {
    MSP_ELIGIBLE: 65,
    PRIVATE: 130
  },
  LAB_TEST: {
    MSP_ELIGIBLE: 20,
    PRIVATE: 100
  },
  SCHOOL_WORK_FORM: {
    FIRST_PAGE: 250,
    ADDITIONAL_PAGE: 75,
    DISABILITY_FLAT_FEE: 750
  }
};

export const getFeeConfig = async () => {
  try {
    const response = await fetch(`${config.service}/config/fees`);
    if (!response.ok) throw new Error('Fee config API failed');
    const configData = await response.json();

    // Cache successful response
    localStorage.setItem('fee-config-cache', JSON.stringify(configData));
    localStorage.setItem('fee-config-timestamp', Date.now().toString());

    return configData;
  } catch (error) {
    console.warn('Fee config API failed, trying cache/defaults:', error);

    // Try cached version first (if less than 24 hours old)
    const cached = localStorage.getItem('fee-config-cache');
    const timestamp = localStorage.getItem('fee-config-timestamp');

    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < 24 * 60 * 60 * 1000) {
        // 24 hours
        console.log('Using cached fee config');
        return JSON.parse(cached);
      }
    }

    // Final fallback to hardcoded defaults
    console.log('Using default fee config');
    return DEFAULT_FEE_CONFIG;
  }
};

// React hook for components

export const useFeeConfig = () => {
  const [config, setConfig] = useState(DEFAULT_FEE_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingDefaults, setIsUsingDefaults] = useState(false);

  useEffect(() => {
    getFeeConfig()
      .then(fetchedConfig => {
        setConfig(fetchedConfig);
        setIsUsingDefaults(false);
      })
      .catch(err => {
        setError(err);
        setConfig(DEFAULT_FEE_CONFIG);
        setIsUsingDefaults(true);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { config, isLoading, error, isUsingDefaults };
};
